import React, { useState, useEffect, useRef } from 'react';
import { ConfettiBackground } from '../components/ThemeEffects';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lightning, Camera, UploadSimple, Clock, Fire } from '@phosphor-icons/react';
import { EventDropLogo } from '../components/EventDropLogo';
import MissionNudge from '../components/MissionNudge';
import { toast } from 'sonner';
import { useEventRecovery, useEventSession } from '../context/EventSessionProvider';
import ReconnectingScreen from '../components/ReconnectingScreen';
import BackButton from '../components/BackButton';
import { isLeaversEdition } from '../shared/packageGuestProducts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChallengePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { event: recoveredEvent, status: recoveryStatus, retry: retryRecovery } = useEventRecovery(slug);
  const { activeEvent } = useEventSession();
  const [event, setEvent] = useState(null);
  const notFound = recoveryStatus === 'failed';
  const [screen, setScreen] = useState('entry');
  const [mode, setMode] = useState('wedding');
  const [active, setActive] = useState(null);
  const [next, setNext] = useState(null);
  const [flash, setFlash] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [myRank, setMyRank] = useState(null);
  const [participantCount, setParticipantCount] = useState(0);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [streak, setStreak] = useState(0);
  const [showReward, setShowReward] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (recoveryStatus === 'ready' && recoveredEvent?.event_id) setEvent(recoveredEvent);
  }, [recoveryStatus, recoveredEvent]);

  // iter197 — Leavers Night never sees the wedding-era entry +
  // mode-selector screens. The "Wedding Mode / Party Mode" picker
  // is constitutionally wrong for a school evening — emoji, ring,
  // hot pink gradients. Skip directly to the live challenge feed
  // with a calm default. The room is one room; the year group
  // doesn't pick a "vibe".
  useEffect(() => {
    if (event && isLeaversEdition(event)) {
      setMode('event');
      setScreen((s) => (s === 'entry' || s === 'mode' ? 'live' : s));
    }
  }, [event]);

  const isLeavers = isLeaversEdition(event);

  // Track challenge open
  useEffect(() => {
    if (screen === 'live' && event) {
      axios.post(`${API}/events/${event.event_id}/challenges/track`, { type: 'open' }).catch(() => {});
    }
  }, [screen, event]);
  // Poll for active + flash challenges
  useEffect(() => {
    if (screen !== 'live' || !event) return;
    const poll = async () => {
      try {
        const [aRes, fRes] = await Promise.all([
          axios.get(`${API}/events/${event.event_id}/challenges/active?mode=${mode}`),
          axios.get(`${API}/events/${event.event_id}/challenges/flash?mode=${mode}`),
        ]);
        setActive(aRes.data.active);
        setNext(aRes.data.next);
        if (fRes.data.flash && !flash) setFlash(fRes.data.flash);
        else if (!fRes.data.flash) setFlash(null);
        // Fetch participant count + leaderboard
        try {
          const [analytics, lb] = await Promise.all([
            axios.get(`${API}/events/${event.event_id}/challenges/analytics`),
            axios.get(`${API}/events/${event.event_id}/leaderboard`),
          ]);
          setParticipantCount(analytics.data.uploads || 0);
          // Find my rank (use first guest as proxy in demo)
          if (lb.data?.length > 0) setMyRank(lb.data[0]);
        } catch {}
      } catch {}
    };
    poll();
    const iv = setInterval(poll, 3000);
    return () => clearInterval(iv);
  }, [screen, event, mode]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !event) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const challengeId = flash?.id || active?.id || '';
      await axios.post(`${API}/events/${event.event_id}/photos?source=challenge`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // Tag with challenge_id
      toast.success("You're in!");
      // Mission tracking: completing a challenge upload ticks the quest.
      try { window.dispatchEvent(new CustomEvent('eventdrop:challenge_upload')); } catch {}
      // Calculate points: base 1 + flash 2x + streak bonus
      const isFlash = !!flash;
      const multiplier = isFlash ? 2 : 1;
      const newStreak = streak + 1;
      const streakBonus = newStreak >= 3 ? 3 : 0;
      const earned = (1 * multiplier) + streakBonus;
      setStreak(newStreak);
      setPointsEarned(earned);
      setShowReward(true);
      setTimeout(() => setShowReward(false), 2500);
      setJustSubmitted(true);
      // Track upload
      axios.post(`${API}/events/${event.event_id}/challenges/track`, { type: 'upload', challenge_id: challengeId }).catch(() => {});
      setTimeout(() => setJustSubmitted(false), 8000);
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  if (notFound) return <ReconnectingScreen status="failed" onRetry={retryRecovery} dashboardHref="/" slug={slug} activeEvent={activeEvent} />;
  if (!event) return <ReconnectingScreen status={recoveryStatus === 'restoring' ? 'restoring' : 'reconnecting'} slug={slug} activeEvent={activeEvent} />;

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(circle at top, #1a0a0a, #15161B)', fontFamily: 'Inter, sans-serif' }} data-testid="challenge-page">
      {/* Universal back-nav — top-left safe area. Always visible on
          entry/mode screens; the live screen has its own dedicated
          top-bar back button so we hide this one there to avoid
          overlap. */}
      {screen !== 'live' && (
        <div style={{ position: 'absolute', top: 18, left: 18, zIndex: 60, paddingTop: 'env(safe-area-inset-top)' }}>
          <BackButton fallbackHref={`/event/${slug}`} label="Back" variant="subtle" testId="challenge-back-btn" />
        </div>
      )}
      {!isLeavers && <ConfettiBackground />}
      {/* Mega Missions nudge — when a guest has a challenge-tier mission running, show countdown. */}
      <MissionNudge event={event} actions={['challenge_upload']} />

      {/* ═══ ENTRY SCREEN ═══ */}
      {screen === 'entry' && (
        <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" style={{ animation: 'cFade .5s ease-out' }} data-testid="challenge-entry">
          <Fire size={56} weight="fill" className="text-[#B89762] mb-6" style={{ filter: 'drop-shadow(0 0 20px rgba(108,92,231,0.4))' }} />
          <h1 className="text-4xl font-black text-white tracking-tight mb-3">Challenge Mode</h1>
          <p className="text-white/40 text-lg mb-8">Turn moments into memories</p>
          <div className="space-y-3 text-left max-w-xs mb-10">
            {['Complete live challenges', 'Capture the funniest moments', 'Be part of the event story'].map((t, i) => (
              <div key={i} className="flex items-center gap-3 text-white/60 text-sm">
                <Lightning size={14} weight="fill" className="text-[#B89762]" /> {t}
              </div>
            ))}
          </div>
          <button onClick={() => setScreen('mode')} className="transition-all active:scale-95" style={{ minHeight: 72, padding: '0 36px', borderRadius: 999, background: 'linear-gradient(135deg, #B89762 0%, #00D4FF 100%)', color: '#FFFFFF', fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', border: 'none', boxShadow: '0 18px 50px rgba(184,151,98,0.5), 0 0 0 1px rgba(255,255,255,0.08) inset', cursor: 'pointer' }} data-testid="join-btn">
            Join Now
          </button>
        </div>
      )}

      {/* ═══ MODE SELECTOR ═══ */}
      {screen === 'mode' && (
        <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" style={{ animation: 'cFade .5s ease-out' }} data-testid="challenge-mode-select">
          <h2 className="text-3xl font-black text-white mb-2">Choose your vibe</h2>
          <p className="text-white/40 mb-10">This sets the challenge style</p>
          <div className="space-y-4 w-full max-w-sm mb-10">
            {[
              { id: 'wedding', label: 'Wedding Mode', sub: 'Fun, social, memorable', emoji: '💍', color: '#B76E79' },
              { id: 'event', label: 'Party Mode', sub: 'Wild, funny, chaotic', emoji: '🎉', color: '#B89762' },
            ].map(m => (
              <button key={m.id} onClick={() => setMode(m.id)}
                className={`w-full p-6 rounded-2xl text-left transition-all ${mode === m.id ? 'border-2' : 'border border-white/10 hover:border-white/20'}`}
                style={{ background: mode === m.id ? `${m.color}15` : 'rgba(255,255,255,0.03)', borderColor: mode === m.id ? m.color : undefined }}
                data-testid={`mode-${m.id}`}>
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{m.emoji}</span>
                  <div>
                    <p className="text-white font-bold text-lg">{m.label}</p>
                    <p className="text-white/40 text-sm">{m.sub}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
          <button onClick={() => setScreen('live')} className="transition-all active:scale-95" style={{ minHeight: 72, padding: '0 40px', borderRadius: 999, background: 'linear-gradient(135deg, #B89762 0%, #00D4FF 100%)', color: '#FFFFFF', fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em', border: 'none', boxShadow: '0 18px 50px rgba(184,151,98,0.5), 0 0 0 1px rgba(255,255,255,0.08) inset', cursor: 'pointer' }} data-testid="enter-btn">
            Enter
          </button>
        </div>
      )}

      {/* ═══ LIVE CHALLENGE ═══ */}
      {screen === 'live' && (
        <div className="min-h-screen flex flex-col" style={{ animation: 'cFade .5s ease-out' }} data-testid="challenge-live">
          {/* Top bar */}
          <div className="px-5 py-4 flex items-center justify-between border-b border-white/5">
            <BackButton fallbackHref={`/event/${slug}`} label="Back" variant="legacy" testId="challenge-live-back-btn" />
            <div className="flex items-center gap-2">
              {isLeavers ? (
                <span className="text-white/50 text-sm font-semibold" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>Mega Missions ✦</span>
              ) : (
                <>
                  <span className="text-sm">{mode === 'wedding' ? '💍' : '🎉'}</span>
                  <span className="text-white/50 text-sm font-semibold">{mode === 'wedding' ? 'Wedding' : 'Party'} Mode</span>
                </>
              )}
            </div>
            {next && (
              <div className="flex items-center gap-1.5 text-[#B89762] text-sm font-semibold">
                <Lightning size={14} weight="fill" /> Next: {next.time}
              </div>
            )}
          </div>

          {/* Main content */}
          <div className="flex-1 flex flex-col items-center justify-center px-6">
            {/* Flash challenge overlay */}
            {flash && (
              <div className="fixed inset-0 z-50 flex items-center justify-center px-6" style={{ background: 'rgba(0,0,0,0.9)', animation: 'cFlash .3s ease-out' }} data-testid="flash-popup">
                <div className="text-center max-w-sm">
                  <Lightning size={48} weight="fill" className="text-[#B89762] mx-auto mb-4" style={{ filter: 'drop-shadow(0 0 20px rgba(108,92,231,0.5))' }} />
                  <p className="text-[#B89762] text-sm font-bold uppercase tracking-wider mb-2">Flash Challenge</p>
                  <h2 className="text-3xl font-black text-white mb-2">{flash.emoji} {flash.title}</h2>
                  <p className="text-yellow-400 font-bold text-sm mb-6">🔥 DOUBLE POINTS ACTIVE</p>
                  <button onClick={() => { setFlash(null); fileRef.current?.click(); }}
                    className="transition-all active:scale-95" style={{ minHeight: 72, padding: '0 40px', borderRadius: 999, background: 'linear-gradient(135deg, #B89762 0%, #00D4FF 100%)', color: '#FFFFFF', fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em', border: 'none', boxShadow: '0 0 36px rgba(184,151,98,0.5), 0 0 0 1px rgba(255,255,255,0.08) inset', cursor: 'pointer' }} data-testid="flash-join">
                    Join Now
                  </button>
                  <button onClick={() => setFlash(null)} className="block mx-auto mt-4 text-white/20 text-sm">Skip</button>
                </div>
              </div>
            )}

            {/* REWARD ANIMATION */}
            {showReward && (
              <div className="fixed inset-0 z-40 flex items-center justify-center pointer-events-none" data-testid="reward-animation">
                <div className="text-center" style={{ animation: 'rewardPop .6s cubic-bezier(0.34,1.56,0.64,1)' }}>
                  <p className="text-5xl font-black text-[#B89762] mb-2">+{pointsEarned} PTS</p>
                  {streak >= 3 && <p className="text-[#B89762] font-bold text-lg">🔥 Streak Bonus!</p>}
                </div>
              </div>
            )}

            {justSubmitted ? (
              <div className="text-center" style={{ animation: 'cFade .4s ease-out' }} data-testid="challenge-submitted">
                <div className="w-20 h-20 rounded-full bg-[#B89762]/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-4xl">✅</span>
                </div>
                <h2 className="text-2xl font-black text-white mb-2">You're in!</h2>
                <p className="text-white/40 mb-6">Next challenge drops soon...</p>
                <div className="flex gap-3 justify-center">
                  <button onClick={() => navigate(`/event/${slug}/leaderboard`)} className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-colors" data-testid="nudge-leaderboard">
                    View Leaderboard
                  </button>
                  {next && (
                    <button onClick={() => setJustSubmitted(false)} className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-[#15161B] text-white/50 border border-white/10 hover:bg-white/10 transition-colors" data-testid="nudge-next">
                    See Next Challenge
                    </button>
                  )}
                </div>
              </div>
            ) : active ? (
              <div className="text-center w-full max-w-sm" data-testid="active-challenge">
                <p className="text-[#B89762] text-sm font-bold uppercase tracking-wider mb-3">Live Now</p>
                <h2 className="text-4xl font-black text-white mb-2">{active.emoji} {active.title}</h2>
                <div className="flex items-center justify-center gap-4 mb-6">
                  <span className="text-white/30 text-sm">{active.label}</span>
                  {participantCount > 0 && <span className="text-white/30 text-sm">👥 {participantCount} participating</span>}
                </div>

                <div className="space-y-3 mb-6">
                  <button onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="w-full flex items-center justify-center gap-3 transition-all active:scale-95 disabled:opacity-50"
                    style={{ minHeight: 72, borderRadius: 22, background: 'linear-gradient(135deg, #B89762 0%, #00D4FF 100%)', color: '#FFFFFF', fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em', border: 'none', boxShadow: '0 16px 40px rgba(184,151,98,0.45), 0 0 0 1px rgba(255,255,255,0.08) inset', cursor: uploading ? 'wait' : 'pointer' }} data-testid="challenge-upload">
                    <Camera size={26} weight="bold" /> {uploading ? 'Uploading...' : 'Record / Upload'}
                  </button>
                </div>

                {/* Rank + progress */}
                {myRank && (
                  <div className="rounded-xl p-4 mb-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="rank-display">
                    <p className="text-white font-bold text-sm">🏆 You are #{myRank.rank}</p>
                    {myRank.gap_to_next > 0 ? (
                      <p className="text-[#B89762] text-xs mt-1">🔥 {myRank.gap_to_next} points to reach #{myRank.next_rank}</p>
                    ) : (
                      <p className="text-amber-400 text-xs mt-1">👑 You're in the lead!</p>
                    )}
                  </div>
                )}

                {/* Streak */}
                {streak >= 2 && (
                  <p className="text-[#B89762] text-sm font-semibold mb-3" data-testid="streak-display">🔥 {streak} uploads in a row! {streak >= 3 ? '+3 bonus points' : ''}</p>
                )}

                {active.remaining_minutes && (
                  <p className="text-white/20 text-sm">Ends in ~{active.remaining_minutes} min</p>
                )}
              </div>
            ) : (
              <div className="text-center" data-testid="no-active">
                <Clock size={48} weight="bold" className="text-white/10 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-white/40 mb-2" style={isLeavers ? { fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: 500 } : undefined}>
                  {isLeavers ? 'The next mission will arrive soon ✦' : 'No active challenge right now'}
                </h2>
                {next && <p className="text-white/20 text-sm">Next: {next.emoji} {next.title} at {next.time}</p>}
              </div>
            )}
          </div>

          {/* Next preview footer */}
          {next && !justSubmitted && (
            <div className="px-6 py-4 border-t border-white/5 text-center">
              <p className="text-white/20 text-xs">Next challenge</p>
              <p className="text-white/40 text-sm font-semibold">{next.emoji} {next.title}</p>
            </div>
          )}

          <input ref={fileRef} type="file" accept="image/*,video/*" capture="environment" className="hidden" onChange={handleUpload} />
        </div>
      )}

      <style>{`
        @keyframes cFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes cFlash { from{opacity:0;transform:scale(1.1)} to{opacity:1;transform:scale(1)} }
        @keyframes rewardPop { from{opacity:0;transform:scale(0.3)} 50%{opacity:1;transform:scale(1.2)} to{opacity:1;transform:scale(1)} }
      `}</style>
    </div>
  );
};

export default ChallengePage;
